import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  HStack,
  Box,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { FiMoreVertical, FiBarChart2, FiSlash, FiGrid } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import type { Link } from '../../types/link';
import { ShortUrlCopy } from './ShortUrlCopy';
import { LinkStatusBadge } from './LinkStatusBadge';
import { formatCompact, formatDate, formatPercent, hostOf } from '../../lib/format';

interface Props {
  links: Link[];
  onExpire: (link: Link) => void;
  onShowQr: (link: Link) => void;
}

export const LinksTable: React.FC<Props> = ({ links, onExpire, onShowQr }) => {
  const navigate = useNavigate();

  return (
    <TableContainer bg="white" borderWidth="1px" rounded="lg" shadow="xs">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th>Link</Th>
            <Th>Destination</Th>
            <Th>Purpose</Th>
            <Th isNumeric>Clicks</Th>
            <Th isNumeric>Installs</Th>
            <Th isNumeric>Install rate</Th>
            <Th>Status</Th>
            <Th>Created</Th>
            <Th aria-label="actions" />
          </Tr>
        </Thead>
        <Tbody>
          {links.map((link) => (
            <Tr
              key={link.id}
              _hover={{ bg: 'gray.50', cursor: 'pointer' }}
              onClick={() => navigate(`links/${link.id}`)}
            >
              <Td maxW="260px">
                <Text fontSize="sm" fontWeight="medium" isTruncated>
                  {link.linkName}
                </Text>
                <ShortUrlCopy url={link.shortUrl} compact />
                {link.campaignName && (
                  <Text fontSize="xs" color="gray.400" isTruncated>
                    {link.campaignName}
                    {link.campaignId ? ` · ${link.campaignId}` : ''}
                  </Text>
                )}
              </Td>
              <Td maxW="220px">
                <ChakraLink href={link.destination} isExternal onClick={(e) => e.stopPropagation()} fontSize="sm">
                  <Text isTruncated>{hostOf(link.destination)}</Text>
                </ChakraLink>
              </Td>
              <Td>
                <Badge bg="var(--brand-50)" color="var(--brand-800)">
                  {link.purpose.replace('_', ' ')}
                </Badge>
              </Td>
              <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCompact(link.metrics.clicks)}
              </Td>
              <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCompact(link.metrics.installs)}
              </Td>
              <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {link.iosAppStoreUrl || link.androidAppStoreUrl ? (
                  formatPercent(link.metrics.installRate)
                ) : (
                  <Text color="gray.300">—</Text>
                )}
              </Td>
              <Td>
                <LinkStatusBadge status={link.status} />
              </Td>
              <Td>
                <Text fontSize="xs" color="gray.500">
                  {formatDate(link.createdAt)}
                </Text>
              </Td>
              <Td>
                <Box onClick={(e) => e.stopPropagation()}>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Row actions"
                      icon={<FiMoreVertical />}
                      size="xs"
                      variant="ghost"
                    />
                    <MenuList>
                      <MenuItem icon={<FiBarChart2 />} onClick={() => navigate(`links/${link.id}`)}>
                        View analytics
                      </MenuItem>
                      <MenuItem icon={<FiGrid />} onClick={() => onShowQr(link)}>
                        Show QR
                      </MenuItem>
                      <MenuItem
                        icon={<FiSlash />}
                        color="red.500"
                        isDisabled={link.status !== 'active'}
                        onClick={() => onExpire(link)}
                      >
                        Disable link
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Box>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {links.length === 0 && (
        <HStack justify="center" py={10}>
          <Text color="gray.400" fontSize="sm">
            No links match these filters.
          </Text>
        </HStack>
      )}
    </TableContainer>
  );
};
